import React, { MouseEvent, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import UploadClientGamePopup from 'components/upload-client-game-popup';
import { selector as productSelector } from 'containers/product-initializer/product-module';
import UserPicture from 'containers/user-picture/user-picture';
import { selector as ratingSystemSelector, switchRatingSystem } from 'redux/modules/rating-system';
import { isBrandedByHockeyTech, userHavePlayerIdAndFeatureMask } from 'selectors/user/user-features-selector';
import { trackEvent, trackedEvents } from 'telemetry/app-analytics';
import { useOutsideClick } from 'utils/hooks/use-outside-click';

import { ProfileControls } from './controls';
import TeamItem from './team-item';
import styles from './profile.module.css';

type ProfileUser = {
    account?: { username?: string | null } | null;
    profile?: { emails?: string[] | null } | null;
};

type ProfileProps = {
    user?: ProfileUser | null;
    logout: () => void;
    onEditEmails: (email: string) => void;
    onEditPassword: () => void;
    onEditVideoPlayer: () => void;
};

/** The user of the header: the picture and name open the profile menu */
const Profile = ({ user, logout, onEditEmails, onEditPassword, onEditVideoPlayer }: ProfileProps) => {
    const dispatch = useDispatch();
    const product = useSelector(productSelector);
    const brandedByHockeyTech: boolean = useSelector(isBrandedByHockeyTech);
    const ratingSystem = useSelector(ratingSystemSelector);
    const userHaveSomeFeatureAndPlayerId: boolean = useSelector(userHavePlayerIdAndFeatureMask);
    const [isDropdownOpened, setIsDropdownOpened] = useState(false);
    const [isUploadPopupOpen, setIsUploadPopupOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const closeDropdown = useCallback(() => setIsDropdownOpened(false), []);
    // The picture and name toggle the menu themselves: they count as inside
    useOutsideClick({ ref: [panelRef, dropdownRef], callback: closeDropdown, isEnabled: isDropdownOpened });

    const toggleDropdown = () => {
        if (!isDropdownOpened) {
            trackEvent(trackedEvents.SHOW_PROFILE_POPUP);
        }
        setIsDropdownOpened(!isDropdownOpened);
    };

    // An action of the menu closes it
    const closeAfter = <TArgs extends unknown[]>(action: (...args: TArgs) => void) => (...args: TArgs) => {
        action(...args);
        closeDropdown();
    };

    const openUploadPopup = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setIsUploadPopupOpen(true);
        closeDropdown();
    };

    return (
        <div className={styles.profileRoot}>
            <UploadClientGamePopup popupOpen={isUploadPopupOpen} close={() => setIsUploadPopupOpen(false)} />
            <div className={styles.profilePanel} ref={panelRef}>
                <div className={styles.avatar} onClick={toggleDropdown}>
                    <UserPicture className={styles.avatarPicture} />
                </div>
                <span className={styles.profileName} onClick={toggleDropdown}>
                    {user?.account?.username}
                </span>
            </div>
            {isDropdownOpened ? (
                <div className={styles.popupMenu} ref={dropdownRef}>
                    <TeamItem />
                    <ProfileControls
                        emails={user?.profile?.emails ?? undefined}
                        onEditEmails={closeAfter(onEditEmails)}
                        onEditPassword={closeAfter(onEditPassword)}
                        onEditVideoPlayer={closeAfter(onEditVideoPlayer)}
                        product={product}
                        logout={logout}
                        handleUploadClientGameClick={openUploadPopup}
                        brandedByHockeyTech={brandedByHockeyTech}
                        switchRatingSystem={nextRatingSystem => dispatch(switchRatingSystem(nextRatingSystem))}
                        ratingSystem={ratingSystem}
                        userHaveSomeFeatureAndPlayerId={userHaveSomeFeatureAndPlayerId}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default Profile;
